'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useTrackVolume } from '@livekit/components-react';
import { cn } from '@/lib/utils';
import { getWidgetBaseUrl } from '@/lib/widget-base';
function getBaseVisuals(state) {
    switch (state) {
        case 'disconnected':
            return { scale: 0.3, opacity: 0.05 };
        case 'connecting':
        case 'initializing':
            return { scale: 0.4, opacity: 0.25 };
        case 'listening':
            return { scale: 0.45, opacity: 0.15 };
        case 'thinking':
            return { scale: 0.5, opacity: 0.25 };
        case 'speaking':
            return { scale: 0.5, opacity: 0.3 };
        default:
            return { scale: 0.3, opacity: 0.05 };
    }
}
export function LottieVisualizer({ state, audioTrack, color, themeMode, className, }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lottieRef = useRef(null);
    const [blinkOn, setBlinkOn] = useState(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [animationData, setAnimationData] = useState(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [LottieComponent, setLottieComponent] = useState(null);
    const isDark = themeMode !== 'light';
    const volume = useTrackVolume(audioTrack, {
        fftSize: 512,
        smoothingTimeConstant: 0.5,
    });
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            import('lottie-react').then((mod) => mod.default),
            fetch(new URL('/lineart-lottie.json', getWidgetBaseUrl()).toString()).then((r) => r.json()),
        ])
            .then(([Lottie, data]) => {
            if (cancelled)
                return;
            setLottieComponent(() => Lottie);
            setAnimationData(data);
        })
            .catch((err) => console.error('Failed to load Lottie visualizer:', err));
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const anim = lottieRef.current;
        if (!anim)
            return;
        if (state === 'disconnected') {
            anim.pause();
        }
        else {
            anim.play();
            anim.setSpeed(1);
        }
    }, [state]);
    const isConnecting = state === 'connecting' || state === 'initializing';
    useEffect(() => {
        if (!isConnecting)
            return;
        const interval = setInterval(() => setBlinkOn((v) => !v), 600);
        return () => clearInterval(interval);
    }, [isConnecting]);
    const base = getBaseVisuals(state);
    let scale = base.scale;
    let opacity = base.opacity;
    if (state === 'speaking') {
        scale = 0.4 + 0.8 * volume;
        opacity = 0.15 + 0.85 * volume;
    }
    if (isConnecting) {
        opacity = blinkOn ? 0.3 : 0.05;
    }
    if (!LottieComponent || !animationData) {
        return _jsx("div", { className: cn('flex items-center justify-center', className) });
    }
    return (_jsx("div", { className: cn('flex items-center justify-center', className), style: color ? { filter: `drop-shadow(0 0 24px ${color}30)` } : undefined, children: _jsx(LottieComponent, { lottieRef: lottieRef, animationData: animationData, loop: true, autoplay: true, className: "origin-center", style: {
                width: '100%',
                height: '100%',
                transform: `scale(${scale})`,
                opacity,
                filter: isDark ? 'none' : 'invert(1)',
                transition: isConnecting
                    ? 'opacity 0.4s ease-in-out'
                    : 'transform 0.15s ease-out, opacity 0.2s ease-out',
            }, rendererSettings: {
                preserveAspectRatio: 'xMidYMid meet',
            } }) }));
}
